import { useContext, useState } from 'react';

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
  Typography,
} from '@mui/material';
import { MuiPickersAdapterContext } from '@mui/x-date-pickers-pro';
import { useQuery } from '@tanstack/react-query';
import { useFlag } from '@unleash/proxy-client-react';
import {
  drawerInviteAtom,
  infoSessionCancelledAtom,
} from 'atoms/inviteSession';
import { formatDistanceToNow } from 'date-fns';
// eslint-disable-next-line import/no-extraneous-dependencies -- we should not use IntlMessageFormat, instead, new crowdin keys should use them inside themselves
import { IntlMessageFormat } from 'intl-messageformat';
import { useSetAtom } from 'jotai';
import { FormattedMessage, useIntl } from 'react-intl';
import { buildGoogleCalendarLink } from 'utils/buildGoogleCalendarLink';
import { formatHour } from 'utils/helpers';
import { FeatureToggle } from 'utils/unleash/toggles';

import { SCREEN_MIN_WIDTH } from 'hooks/useComplaintDevice/useComplaintDevice';
import { getSessionLink } from 'services';

import { RescheduleDropdownButton } from 'components/RescheduleDropdownButton/RescheduleDropdownButton';
import { ConfirmComponentProps } from 'modals/Confirm';

// eslint-disable-next-line import-helpers/order-imports
import { useModals } from 'context';

import { SessionProps } from './types';

import * as S from './styles';

function Session({
  session,
  deleteBooking,
  enduserFullName,
  enduserID,
}: SessionProps) {
  const localization = useContext(MuiPickersAdapterContext);
  const [showModalDeviceNotComplaint, setShowModalDeviceNotComplaint] =
    useState(false);

  const { openModal } = useModals();
  const intl = useIntl();
  const isRescheduleToggleEnabled = useFlag(FeatureToggle.Reschedule);
  const setInfoSessionCancelled = useSetAtom(infoSessionCancelledAtom);
  const setDrawerInvite = useSetAtom(drawerInviteAtom);

  const { data: sessionData } = useQuery({
    queryFn: () => getSessionLink(session.endUserCognitoId),
    queryKey: ['getSessionLink', session.endUserCognitoId],
    enabled: session.canJoin,
  });

  function handleRedirectToMeet() {
    window.open(
      `/new-provider/care-provider/meet?patientCognitoId=${session.endUserCognitoId}&callUrl=${sessionData?.url}`,
      '_blank',
      'noopener noreferrer',
    );
  }

  function handleJoinSession() {
    const isMobile = window.innerWidth < SCREEN_MIN_WIDTH;

    if (isMobile) {
      setShowModalDeviceNotComplaint(true);
      return;
    }

    handleRedirectToMeet();
  }

  function handleDeleteSession(bookingID: string) {
    openModal<ConfirmComponentProps>('confirm', {
      props: {
        message: intl.formatMessage({
          id: 'CALENDAR_EVENT.DELETE_BOOKING_MODAL_TEXT',
        }),
        confirmLabel: intl.formatMessage({
          id: 'CALENDAR_EVENT.CANCEL_CONFIRM',
        }),
        onConfirm: () => {
          deleteBooking(bookingID);
        },
        hideIcon: false,
      },
    });
  }

  function handleRescheduleSession() {
    setInfoSessionCancelled({
      date: new Date(session.start),
      sessionID: session.id,
      enduserID,
      fullName: enduserFullName,
    });

    return setDrawerInvite('invite-schedule');
  }

  return (
    <>
      <Dialog open={showModalDeviceNotComplaint}>
        <DialogTitle>
          {intl.formatMessage({ id: 'CPRESPONSIVE.MODALNOTRECOMMENDED.TITLE' })}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            {intl.formatMessage({
              id: 'CPRESPONSIVE.MODALNOTRECOMMENDED.SUBTITLE',
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={() => {
              setShowModalDeviceNotComplaint(false);
              handleRedirectToMeet();
            }}
          >
            {intl.formatMessage({
              id: 'CPRESPONSIVE.MODALNOTRECOMMENDED.CONTINUEBUTTON',
            })}
          </Button>
          <Button
            variant="text"
            onClick={() => {
              setShowModalDeviceNotComplaint(false);
            }}
          >
            {intl.formatMessage({
              id: 'CPRESPONSIVE.MODALNOTRECOMMENDED.BACKBUTTON',
            })}
          </Button>
        </DialogActions>
      </Dialog>
      <Box
        width="100%"
        display="flex"
        flexDirection="column"
        alignItems="flex-start"
        p={2}
        boxShadow={3}
        bgcolor="secondary.main"
      >
        <Typography variant="body2">
          <S.BookedTo variant="body2">
            <FormattedMessage id="CAREMATRIXCPTC.BOOKINGSESSION.NEXTSESSION" />{' '}
          </S.BookedTo>
          {new IntlMessageFormat(
            '{date, date, ::eeeeeyyyyMMdd} | {date, date, ::jjmm}',
            intl.locale,
          ).format({
            date: new Date(session.start),
          })}{' '}
          -{' '}
          {formatHour(session.end, {
            locale: intl.locale,
            omitMinutesWhenZero: false,
            enableReplaceColonWithH: false,
          })}
        </Typography>
        <Box
          width="100%"
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mt={2}
        >
          <Box display="flex" alignItems="center">
            <Tooltip
              title={intl.formatMessage(
                { id: 'CAREMATRIXCPTC.BOOKINGSESSIONTOOLTIP.SESSIONAVAILABLE' },
                {
                  time: formatDistanceToNow(session.canJoinAfterDate, {
                    locale: localization?.utils?.locale,
                  }),
                },
              )}
              placement="right"
              arrow
              disableHoverListener={session.canJoin}
            >
              <Box marginRight={2}>
                <Button
                  variant="contained"
                  size="small"
                  disabled={!session.canJoin}
                  onClick={handleJoinSession}
                >
                  <FormattedMessage id="CAREMATRIXCPTC.BOOKINGSESSIONBUTTON.GETIN" />
                </Button>
              </Box>
            </Tooltip>
            {isRescheduleToggleEnabled ? (
              <RescheduleDropdownButton
                handleCancelSession={() =>
                  handleDeleteSession(session.bookingId)
                }
                handleRescheduleSession={handleRescheduleSession}
                rescheduledSession={session.rescheduledSession}
              />
            ) : (
              <Button onClick={() => handleDeleteSession(session.bookingId)}>
                <FormattedMessage id="CAREMATRIXCPTC.BOOKINGSESSIONBUTTON.CANCEL" />
              </Button>
            )}
          </Box>

          <Tooltip
            title={intl.formatMessage({
              id: 'CAREMATRIXCPTC.BOOKINGSESSIONTOOLTIP.CALENDAR',
            })}
            placement="left"
            arrow
            onClick={() =>
              buildGoogleCalendarLink(
                session.start,
                session.end,
                enduserFullName,
              )
            }
          >
            <S.CalendarTodayRounded sx={{ cursor: 'pointer' }} />
          </Tooltip>
        </Box>
      </Box>
    </>
  );
}

export default Session;
